'use client';

import { ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useSession } from 'next-auth/react';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { z } from 'zod';
import { cn } from '@/lib/utils';

import { Button } from '@/components/common/inputs/button/button';
import { Input } from '@/components/common/inputs/text/input';
import { Label } from '@/components/common/inputs/text/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/common/inputs/radio/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/inputs/select/select';
import { useFormStore } from '@/lib/hooks/useFormStore';
import { useI18n, useScopedI18n } from '@/locales/client';

// --------------------------------------------------------------------------------
// 1. VALIDATION SCHEMA (Removed leaseType; user must choose everything manually)
// --------------------------------------------------------------------------------
const formDataSchema = z.object({
  userId: z.string().uuid('Invalid user ID'),
  userType: z.string().min(1, 'User type is required'),
  excludedSituation: z.string().min(1, 'Excluded situation is required'),
  propertyOwnership: z.boolean().optional(),
  singleOwnership: z.boolean().optional(),
  spouseOwnership: z.boolean().optional(),
  leaseTermType: z.string().min(1, 'Lease term type is required'),
  leaseDuration: z.number().optional(),
  buildingType: z.string().min(1, 'Building type is required'),
  buildingAgeUnder5: z.string().min(1, 'Building age is required'),
  tenant65Over: z.boolean().nullable(),
  additionalInfo: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export type FormData = z.infer<typeof formDataSchema>;

// Each step's config: includes content rendering & completion check
interface StepConfig {
  title: string;
  content: React.ReactNode;
  isStepComplete: () => boolean;
  skipStep?: () => boolean;
}

// --------------------------------------------------------------------------------
// 3. COMPONENT
// --------------------------------------------------------------------------------
export default function Questionnaire() {
  const { data: session, update } = useSession();
  const t = useI18n();
  const tForm = useScopedI18n('formQuestionnaire');

  const {
    formData: savedFormData,
    formAnswerId,
    isSubmitting,
    isQuestionnaireSubmitted,
    questionnaireSubmission,
    submitForm,
    updateForm,
    setQuestionnaireSubmitted: setSubmitted,
    setQuestionnaireSubmission: setSubmission,
  } = useFormStore();

  // ------------------------
  // State
  // ------------------------
  const [step, setStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isSkipping, setIsSkipping] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // For excluded-situation feedback
  const [isExcludedSituationModalOpen, setIsExcludedSituationModalOpen] =
    useState(false);
  const [excludedSituationFeedback, setExcludedSituationFeedback] =
    useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const defaultFormData: FormData = useMemo(
    () => ({
      userId: session?.user?.id || '',
      userType: '',
      excludedSituation: '',
      propertyOwnership: undefined,
      singleOwnership: undefined,
      spouseOwnership: undefined,
      leaseTermType: '',
      leaseDuration: undefined,
      buildingType: '',
      buildingAgeUnder5: '',
      tenant65Over: null,
      additionalInfo: '',
      firstName: '',
      lastName: '',
    }),
    [session?.user?.id]
  );

  const [formData, setFormData] = useState<FormData>(defaultFormData);

  // --------------------------------------------------------------------------------
  // 4. FETCH EXISTING FORM DATA
  // --------------------------------------------------------------------------------
  useEffect(() => {
    if (session?.user?.id) {
      setFormData((prevData) => ({
        ...prevData,
        userId: session.user.id,
      }));
    }
  }, [session?.user?.id]);

  // Update formData when savedFormData changes
  useEffect(() => {
    if (savedFormData?.answers) {
      setFormData((prevData) => ({
        ...prevData,
        ...savedFormData.answers,
        // Ensure optional fields have proper fallbacks
        additionalInfo: savedFormData.answers.additionalInfo || '',
        propertyOwnership:
          savedFormData.answers.propertyOwnership ?? prevData.propertyOwnership,
        singleOwnership:
          savedFormData.answers.singleOwnership ?? prevData.singleOwnership,
        spouseOwnership:
          savedFormData.answers.spouseOwnership ?? prevData.spouseOwnership,
        leaseDuration:
          savedFormData.answers.leaseDuration ?? prevData.leaseDuration,
      }));

      // If we have complete data and it's not excluded, update submission status
      if (
        savedFormData.answers.userType &&
        savedFormData.answers.excludedSituation &&
        savedFormData.answers.excludedSituation !== 'none'
      ) {
        setSubmission('complete');
        setSubmitted(true);
      }
    } else {
      // Reset to default state if no saved form data
      setFormData(defaultFormData);
      setSubmission('none');
      setSubmitted(false);
    }
  }, [savedFormData, setSubmission, setSubmitted, defaultFormData]);

  // useEffect at the top level of your component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        openTooltip &&
        tooltipRefs.current[openTooltip] &&
        !tooltipRefs.current[openTooltip]!.contains(event.target as Node)
      ) {
        setOpenTooltip(null);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openTooltip]);

  // --------------------------------------------------------------------------------
  // 5. VALIDATION
  // --------------------------------------------------------------------------------
  const validateFormData = (data: FormData): boolean => {
    try {
      formDataSchema.parse(data);
      setValidationErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            errors[err.path[0]] = err.message;
          }
        });
        setValidationErrors(errors);
        toast.error('Please fill in all required fields');
      }
      return false;
    }
  };

  // --------------------------------------------------------------------------------
  // 6. FORM SUBMISSION
  // --------------------------------------------------------------------------------
  const handleSubmit = async (data: FormData) => {
    if (!session?.user?.id || !session?.user?.accessToken) {
      toast.error('Please log in to continue');
      return;
    }

    // Validate entire form before final submission
    if (!validateFormData(data)) return;

    try {
      const formDataWithUser = { ...data, userId: session.user.id };
      let success = false;

      if (!formAnswerId) {
        // Create new
        const response = await submitForm(formDataWithUser);
        success = !!response;
      } else {
        // Update existing
        try {
          success = await updateForm(formDataWithUser, formAnswerId);
        } catch (error: any) {
          if (error.response?.data?.code === 'FORM_ACCESS_DENIED') {
            toast.error('You do not have permission to update this form');
            return;
          }
          toast.error('Failed to update form');
          return;
        }
      }

      // if (success) {
      //   await update(); // Trigger session update
      // }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form');
    }
  };

  // Skip logic: sets form to default values (or partial defaults) and marks submitted
  const handleSkip = async () => {
    if (!session?.user?.id || !session?.user?.accessToken) {
      toast.error('Please log in to continue');
      return;
    }

    setIsSkipping(true);
    try {
      const skipFormData = {
        ...defaultFormData,
        userId: session.user.id,
        userType: 'tenant',
        excludedSituation: 'none',
        leaseTermType: 'undetermined',
        buildingType: 'none',
        buildingAgeUnder5: 'dont-know',
        tenant65Over: false,
        additionalInfo: '',
      };

      // Only call API if form was never submitted before
      if (!formAnswerId && !savedFormData) {
        await submitForm(skipFormData);
      } else {
        // If form was already submitted, just update local state
        setSubmission('skipped');
        setSubmitted(true);
      }

      // await update();
    } catch (error) {
      console.error('Error skipping form:', error);
      toast.error('Failed to skip questionnaire');
    } finally {
      setIsSkipping(false);
    }
  };

  // --------------------------------------------------------------------------------
  // 7. STEPS
  // --------------------------------------------------------------------------------
  const steps: StepConfig[] = [
    // -------------------------------
    // Step 0: Intro
    // -------------------------------
    {
      title: '',
      content: (
        <div className='space-y-8 text-center'>
          <h3 className='text-2xl font-semibold text-gray-900 dark:text-gray-100'>
            {t('home.description')}
          </h3>
          <p className='text-lg text-gray-600 dark:text-gray-300'>
            {tForm('intro.description')}
          </p>
        </div>
      ),
      isStepComplete: () => true,
    },

    // -------------------------------
    // Step 1: Excluded Situation
    // -------------------------------
    {
      title: tForm('q8.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-sm text-gray-700 dark:text-gray-300'>
            {tForm('q8.question')}
          </Label>
          <div className='space-y-2'>
            {[
              {
                id: 'vacation-rental',
                value: 'vacation-rental',
                text: tForm('q8.exceptions.e1'),
                // definition: tForm('q8.definitions.e1'), // add definition here
              },
              {
                id: 'non-residential',
                value: 'non-residential',
                text: tForm('q8.exceptions.e2'),
                // definition: tForm('q8.definitions.e2'),
              },
              {
                id: 'hotel-room',
                value: 'hotel-room',
                text: tForm('q8.exceptions.e3'),
                // definition: tForm('q8.definitions.e3'),
              },
              {
                id: 'landlord-primary-residence',
                value: 'landlord-primary-residence',
                text: tForm('q8.exceptions.e4'),
                definition:
                  'Le bail d’une chambre située dans la résidence principale du locateur, lorsque deux chambres au maximum y sont louées ou offertes en location et que la chambre ne possède ni sortie distincte donnant sur l’extérieur ni installations sanitaires indépendantes de celles utilisées par le locateur (C.C.Q 1892)',
              },
              {
                id: 'healthcare-facility',
                value: 'healthcare-facility',
                text: tForm('q8.exceptions.e5'),
                definition: `Le bail d’une chambre située dans un établissement de santé et de services sociaux, sauf en application de l’article 1974\n`,
              },
              {
                id: 'none',
                value: 'none',
                text: tForm('q8.exceptions.e6'),
                // definition: tForm('q8.definitions.e6'),
              },
            ].map((option) => (
              <div
                key={option.id}
                className={cn(
                  'mb-2 flex items-center justify-between rounded border p-3 transition-colors',
                  formData.excludedSituation === option.value
                    ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/30'
                    : 'border-gray-200 bg-white hover:border-purple-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-purple-700'
                )}
              >
                <div className='flex items-center space-x-2'>
                  <input
                    type='radio'
                    id={option.id}
                    name='excludedSituation'
                    value={option.value}
                    checked={formData.excludedSituation === option.value}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        excludedSituation: e.target.value,
                      })
                    }
                    className='mr-2'
                  />
                  <label
                    htmlFor={option.id}
                    className='text-sm text-gray-700 dark:text-gray-300'
                  >
                    {option.text}
                  </label>
                </div>

                {/* Clickable info button */}
                {option.definition && (
                  <div
                    className='relative'
                    ref={(el) => {
                      tooltipRefs.current[option.id] = el;
                    }}
                  >
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() =>
                        setOpenTooltip(
                          openTooltip === option.id ? null : option.id
                        )
                      }
                      className='text-gray-400 hover:text-gray-600 dark:text-gray-500'
                    >
                      ℹ️
                    </Button>
                    {openTooltip === option.id && (
                      <div className='absolute bottom-full right-0 z-10 mb-2 w-64 rounded bg-gray-700 p-2 text-xs text-white'>
                        {option.definition}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ),
      isStepComplete: () => !!formData.excludedSituation,
    },

    // -------------------------------
    // Step 2: Building Type
    // -------------------------------
    {
      title: tForm('q4.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-gray-700 dark:text-gray-300'>
            {tForm('q4.question')}
          </Label>
          <Select
            value={formData.buildingType}
            onValueChange={(value) =>
              setFormData({ ...formData, buildingType: value })
            }
          >
            <SelectTrigger className='mt-2'>
              <SelectValue placeholder={tForm('q4.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='llm'>{tForm('q4.option1')}</SelectItem>
              <SelectItem value='university'>{tForm('q4.option2')}</SelectItem>
              <SelectItem value='mobile-home'>{tForm('q4.option3')}</SelectItem>
              <SelectItem value='none'>{tForm('q4.option4')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ),
      isStepComplete: () => !!formData.buildingType,
    },

    // -------------------------------
    // Step 3: User Type
    // -------------------------------
    {
      title: tForm('q1.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-gray-700 dark:text-gray-300'>
            {tForm('q1.question')}
          </Label>
          <RadioGroup
            value={formData.userType}
            onValueChange={(value) => {
              setFormData({
                ...formData,
                userType: value,
                propertyOwnership: undefined,
                singleOwnership: undefined,
                spouseOwnership: undefined,
              });
            }}
            className='mt-2 space-y-2'
          >
            <div className='flex flex-col sm:flex-row sm:space-x-8'>
              <div className='mb-4 flex flex-col items-start space-y-2 sm:mb-0'>
                <div className='flex items-center space-x-2 py-2'>
                  <RadioGroupItem
                    value='landlord'
                    id='landlord'
                    className='h-4 w-4'
                  />
                  <Label htmlFor='landlord' className='text-sm sm:text-base'>
                    {tForm('q1.option1')}
                  </Label>
                </div>
              </div>
              <div className='flex flex-col items-start space-y-2'>
                <div className='flex items-center space-x-2 py-2'>
                  <RadioGroupItem
                    value='tenant'
                    id='tenant'
                    className='h-4 w-4'
                  />
                  <Label htmlFor='tenant' className='text-sm sm:text-base'>
                    {tForm('q1.option2')}
                  </Label>
                </div>
                <div className='flex items-center space-x-2 py-2'>
                  <RadioGroupItem
                    value='sub-tenant'
                    id='sub-tenant'
                    className='h-4 w-4'
                  />
                  <Label htmlFor='sub-tenant' className='text-sm sm:text-base'>
                    {tForm('q1.option3')}
                  </Label>
                </div>
                <div className='flex items-center space-x-2 py-2'>
                  <RadioGroupItem
                    value='occupant'
                    id='occupant'
                    className='h-4 w-4'
                  />
                  <Label htmlFor='occupant' className='text-sm sm:text-base'>
                    {tForm('q1.option4')}
                  </Label>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => !!formData.userType,
    },

    // -------------------------------
    // Step 4: Ownership details (Landlord only)
    // -------------------------------
    {
      title: tForm('q2.title'),
      content:
        formData.userType === 'landlord' ? (
          <div className='space-y-4'>
            <Label className='text-gray-700 dark:text-gray-300'>
              {tForm('q2.part1.question')}
            </Label>
            <RadioGroup
              value={String(formData.propertyOwnership)}
              onValueChange={(value) => {
                setFormData({
                  ...formData,
                  propertyOwnership: value === 'true',
                  singleOwnership: undefined,
                  spouseOwnership: undefined,
                });
              }}
              className='mt-2 space-y-2'
            >
              <div className='flex items-center space-x-2 py-2'>
                <RadioGroupItem
                  value='true'
                  id='ownership-yes'
                  className='h-4 w-4'
                />
                <Label htmlFor='ownership-yes' className='text-sm sm:text-base'>
                  {tForm('q2.part1.option1')}
                </Label>
              </div>
              <div className='flex items-center space-x-2 py-2'>
                <RadioGroupItem
                  value='false'
                  id='ownership-no'
                  className='h-4 w-4'
                />
                <Label htmlFor='ownership-no' className='text-sm sm:text-base'>
                  {tForm('q2.part1.option2')}
                </Label>
              </div>
            </RadioGroup>

            {formData.propertyOwnership === true && (
              <div className='mt-4 space-y-4'>
                <Label className='text-gray-700 dark:text-gray-300'>
                  {tForm('q2.part2.question')}
                </Label>
                <RadioGroup
                  value={String(formData.singleOwnership)}
                  onValueChange={(value) => {
                    setFormData({
                      ...formData,
                      singleOwnership: value === 'true',
                      spouseOwnership: undefined,
                    });
                  }}
                  className='mt-2 space-y-2'
                >
                  <div className='flex items-center space-x-2 py-2'>
                    <RadioGroupItem
                      value='true'
                      id='sole-owner'
                      className='h-4 w-4'
                    />
                    <Label
                      htmlFor='sole-owner'
                      className='text-sm sm:text-base'
                    >
                      {tForm('q2.part2.option1')}
                    </Label>
                  </div>
                  <div className='flex items-center space-x-2 py-2'>
                    <RadioGroupItem
                      value='false'
                      id='not-sole-owner'
                      className='h-4 w-4'
                    />
                    <Label
                      htmlFor='not-sole-owner'
                      className='text-sm sm:text-base'
                    >
                      {tForm('q2.part2.option2')}
                    </Label>
                  </div>
                </RadioGroup>

                {formData.singleOwnership === false && (
                  <div className='mt-4 space-y-2'>
                    <Label className='text-gray-700 dark:text-gray-300'>
                      {tForm('q2.part3.question')}
                    </Label>
                    <RadioGroup
                      value={String(formData.spouseOwnership)}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          spouseOwnership: value === 'true',
                        })
                      }
                      className='mt-2 space-y-2'
                    >
                      <div className='flex items-center space-x-2 py-2'>
                        <RadioGroupItem
                          value='true'
                          id='yes-spouse'
                          className='h-4 w-4'
                        />
                        <Label
                          htmlFor='yes-spouse'
                          className='text-sm sm:text-base'
                        >
                          {tForm('q2.part3.option1')}
                        </Label>
                      </div>
                      <div className='flex items-center space-x-2 py-2'>
                        <RadioGroupItem
                          value='false'
                          id='no-spouse'
                          className='h-4 w-4'
                        />
                        <Label
                          htmlFor='no-spouse'
                          className='text-sm sm:text-base'
                        >
                          {tForm('q2.part3.option2')}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null,
      isStepComplete: () =>
        formData.userType !== 'landlord' ||
        (formData.propertyOwnership !== undefined &&
          (formData.propertyOwnership === false ||
            formData.singleOwnership === true ||
            (formData.singleOwnership === false &&
              formData.spouseOwnership !== undefined))),
      skipStep: () => formData.userType !== 'landlord',
    },

    // -------------------------------
    // Step 5: Lease Term
    // -------------------------------
    {
      title: tForm('q3.title'),
      content: (
        <div className='space-y-6'>
          {/* Lease Term Type */}
          <div>
            <Label className='text-gray-700 dark:text-gray-300'>
              {tForm('q3.part2.question')}
            </Label>
            <RadioGroup
              value={formData.leaseTermType}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  leaseTermType: value,
                  leaseDuration:
                    value === 'fixed-term' ? formData.leaseDuration : undefined,
                })
              }
              className='mt-2 space-y-2'
            >
              <div className='flex items-center space-x-2 py-2'>
                <RadioGroupItem
                  value='fixed-term'
                  id='fixed-term'
                  className='h-4 w-4'
                />
                <Label htmlFor='fixed-term' className='text-sm sm:text-base'>
                  {tForm('q3.part2.option1')}
                </Label>
              </div>
              <div className='flex items-center space-x-2 py-2'>
                <RadioGroupItem
                  value='undetermined'
                  id='undetermined'
                  className='h-4 w-4'
                />
                <Label htmlFor='undetermined' className='text-sm sm:text-base'>
                  {tForm('q3.part2.option2')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Lease Duration if fixed-term */}
          {formData.leaseTermType === 'fixed-term' && (
            <div className='mt-4'>
              <Label className='text-gray-700 dark:text-gray-300'>
                {tForm('q3.part3.question')}
              </Label>
              <Input
                type='number'
                value={formData.leaseDuration || ''}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseInt(e.target.value, 10)
                    : undefined;
                  setFormData({
                    ...formData,
                    leaseDuration: value,
                  });
                }}
                placeholder={tForm('q3.part3.placeholder')}
                className='mt-2 w-full max-w-[250px] text-base'
                min={1}
              />
            </div>
          )}
        </div>
      ),
      isStepComplete: () =>
        !!formData.leaseTermType &&
        (formData.leaseTermType !== 'fixed-term' || !!formData.leaseDuration),
    },

    // -------------------------------
    // Step 6: Tenant 65+
    // -------------------------------
    {
      title: tForm('q5.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-gray-700 dark:text-gray-300'>
            {tForm('q5.question')}
          </Label>
          <RadioGroup
            value={
              formData.tenant65Over !== null
                ? String(formData.tenant65Over)
                : ''
            }
            onValueChange={(value) =>
              setFormData({ ...formData, tenant65Over: value === 'true' })
            }
            className='mt-2 space-y-2'
          >
            <div className='flex items-center space-x-2 py-2'>
              <RadioGroupItem value='true' id='over-65' className='h-4 w-4' />
              <Label htmlFor='over-65' className='text-sm sm:text-base'>
                {tForm('q5.option1')}
              </Label>
            </div>
            <div className='flex items-center space-x-2 py-2'>
              <RadioGroupItem value='false' id='under-65' className='h-4 w-4' />
              <Label htmlFor='under-65' className='text-sm sm:text-base'>
                {tForm('q5.option2')}
              </Label>
            </div>
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => formData.tenant65Over !== null,
    },

    // -------------------------------
    // Step 7: Building Age
    // -------------------------------
    {
      title: tForm('q6.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-gray-700 dark:text-gray-300'>
            {tForm('q6.question')}
          </Label>
          <RadioGroup
            value={formData.buildingAgeUnder5}
            onValueChange={(value) =>
              setFormData({ ...formData, buildingAgeUnder5: value })
            }
            className='mt-2 space-y-2'
          >
            <div className='flex items-center space-x-2 py-2'>
              <RadioGroupItem
                value='under-5'
                id='under-5'
                className='h-4 w-4'
              />
              <Label htmlFor='under-5' className='text-sm sm:text-base'>
                {tForm('q6.option1')}
              </Label>
            </div>
            <div className='flex items-center space-x-2 py-2'>
              <RadioGroupItem value='over-5' id='over-5' className='h-4 w-4' />
              <Label htmlFor='over-5' className='text-sm sm:text-base'>
                {tForm('q6.option2')}
              </Label>
            </div>
            <div className='flex items-center space-x-2 py-2'>
              <RadioGroupItem
                value='dont-know'
                id='dont-know'
                className='h-4 w-4'
              />
              <Label htmlFor='dont-know' className='text-sm sm:text-base'>
                {tForm('q6.option3')}
              </Label>
            </div>
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => !!formData.buildingAgeUnder5,
    },

    // -------------------------------
    // Step 8: Additional Info
    // -------------------------------
    {
      title: tForm('q7.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-gray-700 dark:text-gray-300'>
            {tForm('q7.question')}
          </Label>
          <textarea
            className='mt-2 min-h-[100px] w-full rounded border p-3 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
            placeholder={tForm('q7.placeholder')}
            value={formData.additionalInfo || ''}
            onChange={(e) =>
              setFormData({ ...formData, additionalInfo: e.target.value })
            }
            rows={4}
          />
        </div>
      ),
      isStepComplete: () => true,
    },
  ];

  // --------------------------------------------------------------------------------
  // 8. STEP NAVIGATION
  // --------------------------------------------------------------------------------
  const handleNext = () => {
    // If we are on step 1 (excludedSituation) and the user selected anything except 'none',
    // we open the excluded-situation modal and do NOT proceed to the next step
    if (step === 1 && formData.excludedSituation !== 'none') {
      setIsExcludedSituationModalOpen(true);
      return; // Stop here
    }

    let nextStep = step + 1;
    // Skip logic for landlord steps if not landlord, etc.
    while (nextStep < steps.length) {
      const skipCheck = steps[nextStep].skipStep;
      if (skipCheck && skipCheck()) {
        nextStep++;
      } else {
        break;
      }
    }
    if (nextStep < steps.length) {
      setStep(nextStep);
    }
  };

  const handlePrevious = () => {
    let prevStep = step - 1;
    while (prevStep >= 0) {
      const skipCheck = steps[prevStep].skipStep;
      if (skipCheck && skipCheck()) {
        prevStep--;
      } else {
        break;
      }
    }
    if (prevStep >= 0) {
      setStep(prevStep);
    }
  };

  const isStepComplete = steps[step].isStepComplete();

  // --------------------------------------------------------------------------------
  // 9. EXCLUDED-SITUATION MODAL LOGIC
  // --------------------------------------------------------------------------------
  const handleSubmitExcludedFeedback = () => {
    if (excludedSituationFeedback.trim() === '') {
      // optional: show a toast or something that feedback is empty
      setIsExcludedSituationModalOpen(false);
      setStep(0);
      setFormData(defaultFormData);
      return;
    }
    // For now, we simply close the modal, reset to step 0, and show a success msg
    setIsExcludedSituationModalOpen(false);
    setStep(0);
    setFormData(defaultFormData);

    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
    setExcludedSituationFeedback('');
  };

  // --------------------------------------------------------------------------------
  // 10. RENDER
  // --------------------------------------------------------------------------------
  return (
    <>
      {!isQuestionnaireSubmitted && (
        <div className='mx-auto w-full max-w-[800px] rounded-xl bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6 md:p-8'>
          <h2 className='mb-6 text-lg font-semibold text-gray-900 dark:text-gray-100 sm:text-xl'>
            {steps[step].title}
          </h2>

          <div className='mb-8'>{steps[step].content}</div>

          <div className='flex flex-col justify-between space-y-4 sm:flex-row sm:space-y-0'>
            {/* Prev Button */}
            <div className='self-start'>
              {step > 0 && (
                <Button
                  variant='outline'
                  onClick={handlePrevious}
                  className='w-full sm:w-auto'
                >
                  <ChevronLeft
                    className='text-black dark:text-white'
                    strokeWidth={3}
                  />
                  <span className='ml-1'>{tForm('buttons.previous')}</span>
                </Button>
              )}
            </div>

            <div className='flex space-x-2 self-end sm:self-auto'>
              {/* Next or Submit Button */}
              {step < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepComplete}
                  className='w-full bg-purple-600 font-medium text-white hover:bg-purple-700 sm:w-auto'
                >
                  {step === 0
                    ? questionnaireSubmission !== 'none'
                      ? tForm('buttons.edit')
                      : tForm('buttons.begin')
                    : tForm('buttons.next')}
                  <ChevronRight className='ml-1 text-white' strokeWidth={3} />
                </Button>
              ) : (
                <Button
                  onClick={() => handleSubmit(formData)}
                  disabled={!isStepComplete || isSubmitting}
                  className='w-full bg-purple-600 font-medium text-white hover:bg-purple-700 sm:w-auto'
                >
                  {isSubmitting ? (
                    <div className='flex items-center'>
                      <div className='mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white dark:border-purple-400/20 dark:border-t-purple-300' />
                      {tForm('buttons.submitting')}
                    </div>
                  ) : (
                    tForm('buttons.submit')
                  )}
                </Button>
              )}

              {/* Skip Button */}
              <button
                className='rounded border-2 border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'
                onClick={() => setIsModalOpen(true)}
              >
                {tForm('buttons.skip')}
              </button>
            </div>
          </div>

          {/* Skip Modal */}
          {isModalOpen && (
            <div className='fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4 dark:bg-gray-900 dark:bg-opacity-60'>
              <div className='w-full max-w-[500px] rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800 sm:p-6'>
                {questionnaireSubmission === 'complete' ? (
                  <>
                    <p className='mb-4 font-bold text-gray-600 dark:text-gray-300'>
                      {tForm('messages.alreadySubmitted.p1')}
                    </p>
                    <p className='mb-4 text-gray-600 dark:text-gray-300'>
                      {tForm('messages.alreadySubmitted.p2')}
                    </p>
                    <div className='flex flex-col justify-between space-y-3 sm:flex-row sm:space-y-0'>
                      <Button
                        onClick={() => {
                          setIsModalOpen(false);
                          setSubmitted(true);
                        }}
                        className='ml-0 rounded border-2 border-gray-300 bg-white px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 sm:ml-2'
                      >
                        {tForm('buttons.keepPrevious')}
                      </Button>
                      <button
                        className='rounded bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700'
                        onClick={() => setIsModalOpen(false)}
                      >
                        {tForm('buttons.refill')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className='mb-4 font-bold text-gray-600 dark:text-gray-300'>
                      {tForm('messages.defaultSettings.warning')}
                    </p>
                    <ul className='list-inside list-disc text-gray-600 dark:text-gray-300'>
                      {[
                        'settings.p1',
                        'settings.p2',
                        'settings.p3',
                        'settings.p4',
                      ].map((item, index) => (
                        <li key={index} className='mb-4'>
                          <Markdown remarkPlugins={[remarkGfm]}>
                            {tForm(`messages.defaultSettings.${item}` as any)}
                          </Markdown>
                        </li>
                      ))}
                    </ul>
                    <div className='flex flex-col justify-between space-y-3 sm:flex-row sm:space-y-0'>
                      <button
                        className='rounded border-2 border-gray-300 px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'
                        onClick={() => setIsModalOpen(false)}
                      >
                        {tForm('buttons.cancel')}
                      </button>
                      <button
                        className='rounded bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700'
                        onClick={() => {
                          setIsModalOpen(false);
                          handleSkip();
                        }}
                      >
                        {tForm('buttons.skipAnyway')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div className='fixed bottom-4 right-4 max-w-[90%] rounded-lg bg-green-100 p-4 shadow-md dark:bg-green-900 sm:max-w-md'>
          <p className='pr-8 text-green-700 dark:text-green-300'>
            {tForm('messages.feedbackSubmitted' as any)}
          </p>
          <button
            className='absolute right-2 top-2 p-1 text-green-700 dark:text-green-300'
            onClick={() => setShowSuccessMessage(false)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Excluded Situation Modal */}
      {isExcludedSituationModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4 dark:bg-gray-900 dark:bg-opacity-60'>
          <div className='w-full max-w-[500px] rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800 sm:p-6'>
            <h3 className='mb-4 text-base font-bold text-gray-800 dark:text-gray-200 sm:text-lg'>
              {tForm('messages.excludedSituation.title' as any)}
            </h3>
            <p className='mb-4 text-gray-600 dark:text-gray-300'>
              {tForm('messages.excludedSituation.description1' as any)}
            </p>
            <p className='mb-4 text-gray-600 dark:text-gray-300'>
              {tForm('messages.excludedSituation.description2' as any)}
            </p>
            <textarea
              className='mb-4 min-h-[100px] w-full rounded border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
              rows={4}
              value={excludedSituationFeedback}
              onChange={(e) => setExcludedSituationFeedback(e.target.value)}
              placeholder={tForm(
                'messages.excludedSituation.placeholder' as any
              )}
            />
            <div className='flex flex-col justify-between space-y-3 sm:flex-row sm:space-y-0'>
              <button
                className='rounded border-2 border-gray-300 px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'
                onClick={() => setIsExcludedSituationModalOpen(false)}
              >
                {tForm('buttons.cancel')}
              </button>
              <button
                className='rounded bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700'
                onClick={handleSubmitExcludedFeedback}
              >
                {tForm('buttons.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
